import type { CustomEmojiListing } from "@beacon/core/custom-emoji"
import { customIdOf } from "@beacon/core/custom-emoji/names"

/*
 * The company's custom emoji as the web app reads them: a message writes one by name (`:shipit:`),
 * which shows the emoji that has that name now, else one an Admin renamed from it, else the text as
 * written; a reaction holds one by id (`custom:<id>`), which names it whatever it is called now, and
 * by its name alone once it is removed. A removed emoji is never drawn.
 */

export type CustomCatalog = {
  /** The emoji a name (without its colons, any case) shows now, or none. */
  byName(name: string): CustomEmojiListing | undefined
  /** The emoji a reaction's key holds, removed or not, or none for any other key. */
  byKey(key: string): CustomEmojiListing | undefined
  /** Every emoji in the catalogue, not removed, by name: what the picker offers. */
  offered: readonly CustomEmojiListing[]
  /**
   * The emoji a partly typed name finds, likeliest first, `limit` at most: the name it is, then one
   * it starts, then one with a word it starts, then one holding it.
   */
  search(query: string, limit?: number): CustomEmojiListing[]
}

/** The catalogue of `listings`, as the web API lists them. */
export function customCatalogOf(listings: readonly CustomEmojiListing[]): CustomCatalog {
  const offered = listings
    .filter(({ url }) => url !== null)
    .sort((a, b) => a.name.localeCompare(b.name))
  const byId = new Map(listings.map((listing) => [listing.id, listing]))
  const named = new Map(offered.map((listing) => [listing.name, listing]))
  // A former name stands for the earliest added emoji that had it, so adding another never
  // changes what an older message shows.
  const formerly = new Map<string, CustomEmojiListing>()
  for (const listing of [...offered].sort((a, b) => a.addedAt.localeCompare(b.addedAt)))
    for (const former of listing.formerNames)
      if (!formerly.has(former)) formerly.set(former, listing)

  return {
    byName(name) {
      const lower = name.toLowerCase()
      return named.get(lower) ?? formerly.get(lower)
    },
    byKey(key) {
      const id = customIdOf(key)
      return id ? byId.get(id) : undefined
    },
    offered,
    search(query, limit = 8) {
      const q = query.toLowerCase()
      if (q === "") return []
      const rankOf = (name: string) =>
        name === q
          ? 0
          : name.startsWith(q)
            ? 1
            : name.split(/[_-]/).some((word) => word.startsWith(q))
              ? 2
              : name.includes(q)
                ? 3
                : undefined
      return offered
        .flatMap((listing, at) => {
          const rank = rankOf(listing.name)
          return rank === undefined ? [] : [{ listing, rank, at }]
        })
        .sort((a, b) => a.rank - b.rank || a.at - b.at)
        .slice(0, limit)
        .map(({ listing }) => listing)
    },
  }
}
